import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { createOptionListForSelectTag, showToast } from "../../../../helpers/utils/Utils";
import Form from "../../../../components/common/form/Form";
import Modal from "../../../../components/common/form/Modal";
import SelectField from "../../../../components/common/form/SelectField";
import { useGetAllStatesQuery } from "../../../data/states/statesApi";
import { useCreateAutoHoaMutation, useGetAutoHoaByIdQuery, useUpdateAutoHoaMutation } from "../../../hoa/auto-hoa/autoHoa";
import { OptionType } from "../../../../helpers/utils/StyleUtils";
import { useGetAllCountyByStateAbbrQuery } from "../../../data/counties/countiesApi";
import { useGetMetroAreaByStateAbbrQuery } from "../../../data/metroAreas/metroAreasApi";
import { useGetCustomerByIdQuery } from "../../customerApi";
import Button from "../../../../components/common/form/Button";

interface Props {
    formType: 'Add' | 'Update';
    show: boolean;
    setShow: any;
    autoHoaId?: any;
    isReadOnly?: boolean;
}

const CustomerAutoHoaFormField: React.FC<Props> = ({ formType, show, setShow, autoHoaId, isReadOnly = false }) => {

    const { customerId } = useParams<{ customerId: string }>();
    const { data: enums } = useGetEnumsQuery("");
    const hoaData = useGetAutoHoaByIdQuery(autoHoaId);


    const [id, setId] = useState<number | null>(null);
    const [formData, setFormData] = useState<any>([]);
    const stateData = useGetAllStatesQuery("");
    const countyData = useGetAllCountyByStateAbbrQuery(formData?.state?.value);
    const metroAreaData = useGetMetroAreaByStateAbbrQuery(formData?.state?.value);
    const customerData = useGetCustomerByIdQuery(customerId);
    const [createApi, createApiRes] = useCreateAutoHoaMutation();
    const [updateApi, updateApiRes] = useUpdateAutoHoaMutation();


    useEffect(() => {
        if (formType !== 'Add' && hoaData?.data) {
            const obj = hoaData.data;

            const selectedState = obj.county
                ? obj.county.state
                : obj.msa?.state;

            const selectedCounty = obj.county
                ? {
                    label: obj.county.countyName,
                    value: obj.county.id
                }
                : null;

            const selectedMetroArea = obj.msa
                ? {
                    label: obj.msa.name,
                    value: obj.msa.id
                }
                : null;

            const selectedProductType = obj.productType
                ? {
                    label: obj.productType.replaceAll('_', ' '), // Optional: make label readable
                    value: obj.productType
                }
                : null;

            setFormData({
                state: selectedState
                    ? {
                        label: selectedState.stateName,
                        value: selectedState.stateAbbreviation
                    }
                    : null,
                county: selectedCounty,
                metroArea: selectedMetroArea,
                productType: selectedProductType
            });

            setId(obj.id);
        }
    }, [hoaData]);



    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const input = {
            id: id,
            msa: formData?.metroArea ? { id: formData?.metroArea.value } : null,
            county: formData?.county ? { id: formData?.county?.value } : null,
            productType: formData?.productType?.value,
            customerId: customerId,
            tenant: customerData?.data?.tenant,
        };
        if (input) {
            if (!input.county && !input.msa) {
                return showToast("Either County or Metro Area should be selected", "info");
            }
            if (input.county && input.msa) {
                return showToast("Both County and Metro Area cannot be selected", "info");
            }
            if (formType === 'Add') {
                createApi(input);
            } else {
                updateApi({ id: id, requestBody: input });
            }
            setShow(false);
        }
    };


    const handleInputChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;

        if (name == "state") {
            setFormData({
                ...formData,
                county: null,
                metroArea:null,
                [name]: value,
            })

        }
        else {
            setFormData((prevData: any) => ({ ...prevData, [name]: value }));

        }
    };

    return (
        <Modal
            title={formType + " Customer Auto HOA Details"}
            show={show}
            setShow={setShow}
            className="w-2/5 bg-text-0 rounded-lg shadow-lg text-sm"
            fields={
                <Form onSubmit={handleSubmit} fields={
                    <>
                        <SelectField
                            placeholder="Select State..."
                            value={formData.state}
                            options={createOptionListForSelectTag(stateData.data, "stateName", "stateAbbreviation")}
                            onChange={(selectedOption) => handleInputChange({ name: "state", value: selectedOption })}
                            label="State"
                            required
                        />
                        <SelectField
                            placeholder="Select Metro Area..."
                            value={formData.metroArea}
                            options={createOptionListForSelectTag(metroAreaData.data, "name", "id")}
                            onChange={(selectedOption) => handleInputChange({ name: "metroArea", value: selectedOption })}
                            label="Metro Area"
                        />
                        <SelectField
                            placeholder="Select County..."
                            value={formData.county}
                            options={createOptionListForSelectTag(countyData.data, "countyName", "id")}
                            onChange={(selectedOption) => handleInputChange({ name: "county", value: selectedOption })}
                            label="County"
                        />
                        <SelectField
                            placeholder="Select productType..."
                            value={formData?.productType || null}
                            options={createOptionListForSelectTag(enums?.["ProductType"], "value", "constant")}
                            onChange={(selectedOption) => handleInputChange({ name: "productType", value: selectedOption })}
                            label="Product Type"
                            required
                        />
                        <div className="mt-4 border-t border-t-surface-5 flex justify-end pt-4 gap-3">
                            <Button btnText="Cancel" type="close" onClick={() => setShow(false)} />
                            <Button
                                btnText={formType}
                                isLoading={autoHoaId ? updateApiRes.isLoading : createApiRes.isLoading}
                                type="submit"
                            />
                        </div>
                    </>
                }
                />
            }
        />
    );
};

export default CustomerAutoHoaFormField;